import React, { useCallback, useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import { remove, update } from 'ramda'
import CreateVolumeBackendConfigurationModal from './CreateVolumeBackendConfigurationModal'
import Text from 'core/elements/Text'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import Tooltip from 'core/elements/tooltip'
import clsx from 'clsx'
import { trackEvent } from 'utils/tracking'
import { ErrorMessage } from 'core/components/validatedForm/ErrorMessage'

const useStyles = makeStyles((theme: Theme) => ({
  cards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: theme.spacing(2),
    minHeight: 108,
    boxSizing: 'border-box',
    width: '100%',
    [theme.breakpoints.up(1500)]: {
      gridTemplateColumns: 'repeat(4, 1fr)',
    },
  },
  addConfigCard: {
    border: `1px dashed ${theme.components.table.border}`,
    borderRadius: 4,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(3),
    minHeight: 108,
    cursor: 'pointer',
    transition: 'border-color 0.2s ease',
    boxSizing: 'border-box',
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
  },
  addConfigCardBody: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    textAlign: 'center',
  },
  configCard: {
    border: `1px solid ${theme.components.table.border}`,
    borderRadius: 4,
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'row',
    backgroundColor: theme.palette.grey[50],
    gap: theme.spacing(2),
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    flex: 1,
    justifyContent: 'center',
  },
  cardActions: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: '8px',
  },
  cardRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(0.5),
  },
  labelText: {
    color: theme.palette.grey[700],
    whiteSpace: 'nowrap',
  },
  valueText: {
    fontWeight: 600,
  },
  iconButton: {
    border: `1px solid ${theme.components.table.border}`,
    borderRadius: 8,
    width: 36,
    height: 36,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    backgroundColor: theme.palette.common.white,
    '&:hover': {
      backgroundColor: theme.palette.grey[100],
    },
  },
  editIcon: {
    color: theme.palette.grey[600],
    fontSize: 18,
    fontWeight: 500,
  },
  removeIcon: {
    color: theme.palette.grey[600],
    fontSize: 18,
    fontWeight: 500,
  },
  noCursor: {
    color: `${theme.components.badge.unknown.color} !important`,
    cursor: 'not-allowed',
  },
  plusIcon: {
    color: theme.palette.blue[900],
    marginBottom: theme.spacing(1),
    fontWeight: 500,
  },
  errorText: {
    color: theme.palette.error.main,
  },
}))

const driverLabels = {
  Ceph: 'Ceph',
  LVM: 'LVM',
  NFS: 'NFS',
  NetApp: 'NetApp Data ONTAP',
  PureStorageISCSI: 'Pure Storage ISCSI',
  PureStorageFC: 'Pure Storage FC',
  PureStorageNVME: 'Pure Storage NVME',
  HitachiISCSI: 'Hitachi ISCSI',
  HitachiFC: 'Hitachi FC',
  InfinidatISCSI: 'Infinidat ISCSI',
  HuaweiISCSI: 'Huawei Dorado ISCSI',
  HuaweiFC: 'Huawei Dorado FC',
  HPE3PARISCSI: 'HPE 3PAR ISCSI',
  HPE3PARFC: 'HPE 3PAR FC',
  Tintri: 'Tintri NFS',
  Custom: 'Custom',
}

export default function VolumeBackendConfigurationCards({
  params,
  updateParams,
  volumeBackend,
  usedVolumeBackends,
  filteredConfigs,
}) {
  const classes = useStyles()
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedConfig, setSelectedConfig] = useState(null)
  const [selectedConfigIndex, setSelectedConfigIndex] = useState(null)
  const clickAddConfig = () => {
    setSelectedConfig(null)
    setModalOpen(true)
  }

  const editConfigFn = (config, idx) => {
    setSelectedConfig(config)
    setSelectedConfigIndex(idx)
    setModalOpen(true)
  }

  const updateConfigsFn = useCallback(
    (config) => {
      if (selectedConfigIndex !== null) {
        updateParams({
          volumeBackendConfigs: update(
            selectedConfigIndex,
            { ...selectedConfig, ...config },
            params.volumeBackendConfigs,
          ),
        })
      } else {
        updateParams({ volumeBackendConfigs: [...params.volumeBackendConfigs, config] })
      }
    },
    [selectedConfig, selectedConfigIndex, params.volumeBackendConfigs],
  )

  const removeConfigFn = useCallback(
    (network, idx) => {
      updateParams({ volumeBackendConfigs: remove(idx, 1, params.volumeBackendConfigs) })
      trackEvent(`Cluster Blueprint - Clicked Remove Volume Backend Configuration`)
    },
    [params.volumeBackendConfigs],
  )

  const closeModal = () => {
    setModalOpen(false)
    setSelectedConfig(null)
    setSelectedConfigIndex(null)
  }

  return (
    <>
      {modalOpen && (
        <CreateVolumeBackendConfigurationModal
          volumeConfig={selectedConfig}
          currentVolumeConfigs={params.volumeBackendConfigs}
          updateVolumeConfigs={updateConfigsFn}
          onClose={closeModal}
          volumeBackend={volumeBackend}
          matchingBackendConfigs={filteredConfigs}
        />
      )}
      {filteredConfigs?.length === 0 ? (
        <ErrorMessage>
          <Text variant="body2" className={classes.errorText}>
            You must add at least one Volume Backend Configuration to save this Volume Backend.
          </Text>
        </ErrorMessage>
      ) : null}
      <div className={classes.cards}>
        {filteredConfigs.map((config, idx) => {
          const deletable = !usedVolumeBackends.includes(config.name)
          const keyValueCount = config?.keyValues?.length || 0
          return (
            <div key={idx} className={classes.configCard}>
              <div className={classes.cardContent}>
                <div className={classes.cardRow}>
                  <Text variant="body2" className={classes.labelText}>
                    Name:
                  </Text>
                  <Text variant="body2" className={classes.valueText}>
                    {config?.name}
                  </Text>
                </div>
                <div className={classes.cardRow}>
                  <Text variant="body2" className={classes.labelText}>
                    Storage Driver:
                  </Text>
                  <Text variant="body2" className={classes.valueText}>
                    {driverLabels[config?.driver]}
                  </Text>
                </div>
                <div className={classes.cardRow}>
                  <Text variant="body2" className={classes.labelText}>
                    Key Value Configurations:
                  </Text>
                  <Text variant="body2" className={classes.valueText}>
                    {keyValueCount}
                  </Text>
                </div>
              </div>
              <div className={classes.cardActions}>
                <div
                  className={classes.iconButton}
                  onClick={() => editConfigFn(config, config.index)}
                  data-testid="edit-volume-backend-config-button"
                >
                  <FontAwesomeIcon className={classes.editIcon}>pen-to-square</FontAwesomeIcon>
                </div>
                <Tooltip message={deletable ? '' : 'This configuration is currently in-use'}>
                  <div
                    className={clsx(classes.iconButton, !deletable && classes.noCursor)}
                    onClick={() => {
                      if (!deletable) {
                        return
                      }
                      removeConfigFn(config, config.index)
                    }}
                    data-testid="delete-volume-backend-config-button"
                  >
                    <FontAwesomeIcon className={classes.removeIcon}>trash-can</FontAwesomeIcon>
                  </div>
                </Tooltip>
              </div>
            </div>
          )
        })}
        <div
          className={classes.addConfigCard}
          onClick={clickAddConfig}
          data-testid="add-volume-backend-config-button"
        >
          <div className={classes.addConfigCardBody}>
            <FontAwesomeIcon size="lg" className={classes.plusIcon}>
              plus
            </FontAwesomeIcon>
            <Text variant="body2">
              Add Volume Backend <br /> Configuration
            </Text>
          </div>
        </div>
      </div>
    </>
  )
}
